import { agencyIdFromPath } from '../../features/auth/agencyAccess';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  Bug,
  ChevronDown,
  Crosshair,
  Gauge,
  Film,
  Menu,
  Megaphone,
  Network,
  PackageOpen,
  PanelLeft,
  Search,
  ScrollText,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Button, cn, ScrollArea, Sheet, SheetContent } from '@shigo-ui/ui';

import { NetworkBrandLogo } from '../branding/network-brand-logo';
import { NetworkBrandLogoTone } from '../branding/network-brand-logo.types';
import { ShigoPermission } from '../../features/auth/permissions';
import {
  publisherDashboardPath,
  publisherIdFromPath,
  resolveOwnedDashboardContext,
  ShigoDashboardContext,
} from '../../features/auth/dashboardDestination';
import { useShigoPermissions } from '../../features/auth/useShigoPermissions';
import { useNetworkFeatures } from '../../features/auth/useNetworkFeatures';
import { CommandPalette } from '../../features/search/CommandPalette';
import { CommandPaletteTrigger } from '../../features/search/CommandPaletteTrigger';
import { openCommandPalette } from '../../features/search/commandPaletteStore';
import { useCommandPaletteAvailability } from '../../features/search/useCommandPaletteAvailability';
import { dashboardT } from '../../lib/messages';
import { useShigoSession } from '../../lib/useShigoSession';
import { useShigoTheme } from '../../lib/useShigoTheme';
import { ShigoAccountMenu } from './ShigoAccountMenu';
import { ThemeModeToggle } from './ThemeModeToggle';
import { Breadcrumbs } from './Breadcrumbs';
import { readShigoAppConfig } from '../../lib/config';
import { ShigoDeploymentTarget } from '../../lib/deployment';
import {
  closeMobileNavigation,
  openMobileNavigation,
  useMobileNavigationOpen,
} from './mobileNavigationStore';
import { ShigoNavigationItem } from './shigo-navigation.types';
import {
  isNavigationPathActive,
  resolveVisibleNavigationGroups,
  resolveVisibleNavigationLinks,
  shigoNavigationGroups,
  shigoNavigationLinks,
  type ShigoNavigationGroupItem,
} from './shigo-navigation.config';
import { offsetTopWithin } from './navigationIndicator';

const navigationGroupIcons: Record<ShigoNavigationGroupItem, typeof PackageOpen> = {
  [ShigoNavigationItem.Supply]: PackageOpen,
  [ShigoNavigationItem.Demand]: Gauge,
  [ShigoNavigationItem.Campaigns]: Megaphone,
  [ShigoNavigationItem.Targeting]: Crosshair,
  [ShigoNavigationItem.A2v]: Film,
  [ShigoNavigationItem.Reporting]: ScrollText,
  [ShigoNavigationItem.Admin]: ShieldCheck,
};

const navigationLinkIcons: Partial<Record<ShigoNavigationItem, typeof Bug>> = {
  [ShigoNavigationItem.Debug]: Bug,
  [ShigoNavigationItem.Networks]: Network,
};

const navigationLinkClassName =
  'shigo-navigation-link text-text-secondary hover:text-foreground relative isolate flex items-center rounded-md text-sm font-medium transition-colors';

/**
 * The selected item's fill: one element under the list, moved with a CSS transform transition
 * (compositor-only, so it stays smooth while the next route loads). It is re-measured when the
 * route changes and whenever the list resizes, e.g. while a group springs open.
 */
function useNavigationActiveIndicator(pathname: string) {
  const listRef = useRef<HTMLUListElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;
    function place() {
      if (!list || !indicator) return;
      const active = list.querySelector<HTMLElement>('.shigo-navigation-link--active');
      indicator.style.opacity = active ? '1' : '0';
      if (!active) return;
      indicator.style.height = `${String(active.offsetHeight)}px`;
      indicator.style.transform = `translateY(${String(offsetTopWithin(active, list))}px)`;
    }
    if (!indicator.dataset['placed']) {
      // First placement jumps; later ones glide.
      indicator.style.transition = 'none';
      place();
      void indicator.offsetHeight;
      indicator.style.transition = '';
      indicator.dataset['placed'] = 'true';
    } else {
      place();
    }
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(place);
    observer.observe(list);
    return () => {
      observer.disconnect();
    };
  }, [pathname]);
  return { listRef, indicatorRef };
}

type NavigationContentProps = {
  onNavigate?: () => void;
};

function NavigationContent({ onNavigate }: NavigationContentProps) {
  const config = readShigoAppConfig();
  const targetLabel = dashboardT(
    config.target === ShigoDeploymentTarget.Production
      ? 'nav.networkPill.target.prod'
      : 'nav.networkPill.target.dev',
  );
  const networkLabel = `${config.displayName} · ${targetLabel}`;
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { listRef, indicatorRef } = useNavigationActiveIndicator(pathname);
  const [groupExpansionOverrides, setGroupExpansionOverrides] = useState<
    Partial<Record<ShigoNavigationGroupItem, boolean>>
  >({});
  const { can } = useShigoPermissions();
  const { isFeatureAllowed } = useNetworkFeatures();
  const { session } = useShigoSession();
  const agencyId = agencyIdFromPath(pathname);
  const publisherId = publisherIdFromPath(pathname);
  const isPublisherContext = publisherId !== null;
  const ownedDashboardContext = session ? resolveOwnedDashboardContext(session.user) : null;
  let dashboardContext = ownedDashboardContext;
  if (isPublisherContext) dashboardContext = ShigoDashboardContext.Publisher;
  if (agencyId) dashboardContext = ShigoDashboardContext.Agency;
  const visibleNavigationGroups = resolveVisibleNavigationGroups(shigoNavigationGroups, {
    dashboardContext,
    can,
    isFeatureAllowed,
  });
  const visibleNavigationLinks = resolveVisibleNavigationLinks(shigoNavigationLinks, {
    dashboardContext,
    can,
    isFeatureAllowed,
  });
  const publisherLandingPath = publisherDashboardPath(session?.user.permissions ?? []);

  let homeDestination: {
    to: '/' | '/a/$agencyId' | typeof publisherLandingPath;
    params: { agencyId?: string; publisherId?: string };
  } = { to: '/', params: {} };
  if (publisherId) homeDestination = { to: publisherLandingPath, params: { publisherId } };
  if (agencyId) homeDestination = { to: '/a/$agencyId', params: { agencyId } };

  function toggleGroup(groupId: ShigoNavigationGroupItem, routeIsActive: boolean) {
    setGroupExpansionOverrides((current) => ({
      ...current,
      [groupId]: !(current[groupId] ?? routeIsActive),
    }));
  }
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-sidebar-border bg-sidebar flex h-16 items-center border-b px-5">
        <Link aria-label={dashboardT('app.name')} {...homeDestination} onClick={onNavigate}>
          <NetworkBrandLogo className="h-7 w-40" height={28} tone={NetworkBrandLogoTone.Light} />
        </Link>
      </div>

      <ScrollArea
        type="hover"
        scrollHideDelay={700}
        className="[&_[data-orientation=vertical]>div]:bg-sidebar-foreground/25 [&_[data-orientation=vertical]:hover>div]:bg-sidebar-foreground/45 min-h-0 flex-1 [&_[data-orientation=vertical]]:w-2"
      >
        <nav aria-label={dashboardT('nav.primary')} className="px-3 py-5">
          <ul ref={listRef} className="relative isolate space-y-1">
            <span
              ref={indicatorRef}
              aria-hidden="true"
              className="bg-sidebar-accent ease-fluid pointer-events-none absolute inset-x-0 top-0 -z-10 rounded-md opacity-0 transition-transform duration-[var(--duration-fluid)] motion-reduce:transition-none"
            />
            {agencyId ||
            (isPublisherContext
              ? can(ShigoPermission.PublisherDashboardRead) &&
                can(ShigoPermission.PublisherDashboardHomeRead)
              : can(ShigoPermission.HomeRead)) ? (
              <li>
                <Link
                  {...homeDestination}
                  activeOptions={{ exact: true }}
                  onClick={onNavigate}
                  className={cn(navigationLinkClassName, 'h-10 gap-3 px-3')}
                  activeProps={{ className: 'shigo-navigation-link--active' }}
                >
                  <BarChart3 className="h-4 w-4" />
                  <span>{dashboardT('nav.dashboard')}</span>
                </Link>
              </li>
            ) : null}
            {visibleNavigationLinks
              .filter(({ id }) => id === ShigoNavigationItem.Networks)
              .map((link) => {
                const LinkIcon = navigationLinkIcons[link.id];
                const linkIsActive = isNavigationPathActive(link.activePaths, pathname);

                return (
                  <li key={link.id}>
                    <Link
                      to={link.to}
                      onClick={onNavigate}
                      className={cn(
                        navigationLinkClassName,
                        'h-10 gap-3 px-3',
                        linkIsActive && 'shigo-navigation-link--active',
                      )}
                      data-status={linkIsActive ? 'active' : undefined}
                    >
                      {LinkIcon ? <LinkIcon className="h-4 w-4" aria-hidden="true" /> : null}
                      <span>{dashboardT(link.labelKey)}</span>
                    </Link>
                  </li>
                );
              })}
            {visibleNavigationGroups.map((group) => {
              const GroupIcon = navigationGroupIcons[group.id];
              const groupRouteIsActive = isNavigationPathActive(group.activePaths, pathname);
              const groupIsExpanded = groupExpansionOverrides[group.id] ?? groupRouteIsActive;

              return (
                <li key={group.id}>
                  <Button
                    aria-expanded={groupIsExpanded}
                    className="shigo-navigation-group text-text-secondary hover:bg-muted hover:text-foreground h-10 w-full justify-start gap-3 rounded-md px-3 text-sm font-medium"
                    onClick={() => {
                      toggleGroup(group.id, groupRouteIsActive);
                    }}
                    variant="ghost"
                    size="lg"
                  >
                    <GroupIcon className="h-4 w-4" />
                    <span>{dashboardT(group.labelKey)}</span>
                    <ChevronDown
                      aria-hidden="true"
                      className={cn(
                        'ease-fluid ml-auto h-4 w-4 transition-transform duration-[var(--duration-fluid)] motion-reduce:transition-none',
                        !groupIsExpanded && '-rotate-90',
                      )}
                    />
                  </Button>
                  {groupIsExpanded ? (
                    // Opens at once (no height animation, which re-lays out every frame); the
                    // entries fade and settle in on the compositor via @starting-style.
                    <ul className="space-y-1 pt-1 transition-[opacity,translate] duration-200 ease-out motion-reduce:transition-none starting:-translate-y-1 starting:opacity-0">
                      {group.entries.map((entry) => {
                        const entryIsActive = isNavigationPathActive(entry.activePaths, pathname);

                        return (
                          <li key={entry.id}>
                            <Link
                              to={entry.to}
                              params={{
                                publisherId: publisherId ?? '',
                                agencyId: agencyId ?? '',
                                section: entry.section ?? '',
                              }}
                              onClick={onNavigate}
                              className={cn(
                                navigationLinkClassName,
                                'h-9 py-2 pr-3 pl-10',
                                entryIsActive && 'shigo-navigation-link--active',
                              )}
                              data-status={entryIsActive ? 'active' : undefined}
                            >
                              <span>{dashboardT(entry.labelKey)}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
            {visibleNavigationLinks
              .filter(({ id }) => id === ShigoNavigationItem.Debug)
              .map((link) => {
                const LinkIcon = navigationLinkIcons[link.id];
                const linkIsActive = isNavigationPathActive(link.activePaths, pathname);

                return (
                  <li key={link.id}>
                    <Link
                      to={link.to}
                      onClick={onNavigate}
                      className={cn(
                        navigationLinkClassName,
                        'h-10 gap-3 px-3',
                        linkIsActive && 'shigo-navigation-link--active',
                      )}
                      data-status={linkIsActive ? 'active' : undefined}
                    >
                      {LinkIcon ? <LinkIcon className="h-4 w-4" aria-hidden="true" /> : null}
                      <span>{dashboardT(link.labelKey)}</span>
                    </Link>
                  </li>
                );
              })}
          </ul>
        </nav>
      </ScrollArea>
      <footer className="border-sidebar-border shrink-0 border-t px-3 py-3">
        <div
          aria-label={dashboardT('nav.networkPill.label')}
          title={networkLabel}
          className="text-sidebar-foreground/80 inline-flex max-w-full items-center gap-2 px-3 py-1 text-xs font-medium"
        >
          <span
            aria-hidden="true"
            className="bg-sidebar-foreground/50 h-1.5 w-1.5 shrink-0 rounded-full"
          />
          <span className="truncate">{networkLabel}</span>
        </div>
      </footer>
    </div>
  );
}

export interface ShigoNavigationProps {
  expanded: boolean;
}

export function ShigoNavigation({ expanded }: ShigoNavigationProps) {
  const mobileNavigationOpen = useMobileNavigationOpen();

  return (
    <>
      <aside
        className={cn(
          'border-sidebar-border bg-sidebar text-sidebar-foreground hidden h-dvh shrink-0 border-r lg:block',
          expanded ? 'w-64' : 'w-0 overflow-hidden border-r-0',
        )}
        data-state={expanded ? 'expanded' : 'collapsed'}
        data-testid="desktop-navigation"
      >
        {expanded ? <NavigationContent /> : null}
      </aside>

      <Sheet
        open={mobileNavigationOpen}
        onOpenChange={(open) => {
          if (open) openMobileNavigation();
          else closeMobileNavigation();
        }}
      >
        <SheetContent
          side="left"
          showCloseButton={false}
          className="border-sidebar-border bg-sidebar text-sidebar-foreground w-72 p-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:max-w-none"
        >
          <Button
            aria-label={dashboardT('nav.close')}
            size="icon"
            variant="ghost"
            className="absolute top-[calc(0.75rem+env(safe-area-inset-top))] right-3 z-10"
            onClick={closeMobileNavigation}
          >
            <X className="h-5 w-5" />
          </Button>
          <NavigationContent onNavigate={closeMobileNavigation} />
        </SheetContent>
      </Sheet>
    </>
  );
}

export interface ShigoPageHeaderProps {
  navigationExpanded: boolean;
  onNavigationToggle: () => void;
  title: string;
}

export function ShigoPageHeader({
  navigationExpanded,
  onNavigationToggle,
  title,
}: ShigoPageHeaderProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { themeMode, setThemeMode } = useShigoTheme();
  const isCommandPaletteAvailable = useCommandPaletteAvailability();

  return (
    <header className="border-border bg-card flex h-[calc(4rem+env(safe-area-inset-top))] shrink-0 items-center gap-3 border-b px-4 pt-[env(safe-area-inset-top)]">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Button
          aria-label={dashboardT('nav.open')}
          className="h-8 w-8 shrink-0 lg:hidden"
          onClick={openMobileNavigation}
          size="icon"
          variant="ghost"
        >
          <Menu aria-hidden="true" className="h-5 w-5" />
        </Button>
        <Button
          aria-label={dashboardT(navigationExpanded ? 'nav.collapse' : 'nav.expand')}
          aria-pressed={navigationExpanded}
          className="hidden h-8 w-8 shrink-0 lg:inline-flex"
          onClick={onNavigationToggle}
          size="icon"
          variant="ghost"
        >
          <PanelLeft className="h-5 w-5" aria-hidden="true" />
        </Button>
        <h1 className="sr-only">{title}</h1>
        <Breadcrumbs rootTitle={title} />
      </div>

      {isCommandPaletteAvailable ? (
        <div className="hidden shrink-0 xl:block">
          <CommandPaletteTrigger />
        </div>
      ) : null}
      {isCommandPaletteAvailable ? (
        <Button
          aria-label={dashboardT('search.placeholder')}
          className="h-8 w-8 shrink-0 xl:hidden"
          onClick={openCommandPalette}
          size="icon"
          variant="ghost"
        >
          <Search aria-hidden="true" className="h-5 w-5" />
        </Button>
      ) : null}
      {isCommandPaletteAvailable ? <CommandPalette /> : null}

      <div className="flex shrink-0 items-center justify-end gap-3">
        <ThemeModeToggle className="hidden sm:grid" onChange={setThemeMode} themeMode={themeMode} />
        <ShigoAccountMenu pathname={pathname} />
      </div>
    </header>
  );
}
